import { useCallback, useEffect, useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { EmptyState } from '@/components/ui/EmptyState';
import { container } from '@/services/container';
import { useIdentityStore } from '@/store/identity';
import { useUiStore } from '@/store/ui';
import { shortId } from '@/lib/format';
import { MeshLinkError } from '@/lib/result';
import type { Address, DexPool, DexOrder, DexTrade, DexCandle } from '@shared/chain';

const fmt = (v: unknown): string => (typeof v === 'number' ? v.toLocaleString('zh-CN', { maximumFractionDigits: 6 }) : String(v ?? '—'));

/** 迷你走势（最近 N 根 K 线收盘价，SVG）。 */
function Spark({ candles }: { candles: DexCandle[] }): JSX.Element | null {
  if (candles.length < 2) return null;
  const closes = candles.map((c) => Number(c.close ?? 0));
  const min = Math.min(...closes);
  const max = Math.max(...closes);
  const span = max - min || 1;
  const pts = closes.map((v, i) => `${(i / (closes.length - 1)) * 120},${26 - ((v - min) / span) * 22}`).join(' ');
  const up = closes[closes.length - 1] >= closes[0];
  return (
    <svg viewBox="0 0 120 30" className="h-8 w-32" aria-hidden>
      <polyline points={pts} fill="none" stroke={up ? '#10b981' : '#ef4444'} strokeWidth="1.5" />
    </svg>
  );
}

/** DEX 交易大厅（生态门户集成）。 */
export function DexPage(): JSX.Element {
  const address = useIdentityStore((s) => s.address);
  const [pools, setPools] = useState<DexPool[]>([]);
  const [sel, setSel] = useState('');
  const [candles, setCandles] = useState<DexCandle[]>([]);
  const [orders, setOrders] = useState<DexOrder[]>([]);
  const [trades, setTrades] = useState<DexTrade[]>([]);
  const [busy, setBusy] = useState(false);

  // swap
  const [side, setSide] = useState<'in' | 'out'>('in');
  const [amt, setAmt] = useState('');
  // order
  const [oSide, setOSide] = useState<'buy' | 'sell'>('buy');
  const [oToken, setOToken] = useState('');
  const [oPrice, setOPrice] = useState('');
  const [oAmt, setOAmt] = useState('');

  const pool = pools.find((p) => p.pool_address === sel);

  const load = useCallback(async (): Promise<void> => {
    try {
      const [mk] = await Promise.all([container.chain.dexMarkets().catch(() => [])]);
      setPools(mk ?? []);
      if (!sel && mk && mk.length > 0) setSel(mk[0].pool_address as string);
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    }
  }, [sel]);

  const loadDetail = useCallback(async (): Promise<void> => {
    if (!sel) return;
    try {
      const [c, o, t] = await Promise.all([
        container.chain.dexCandles(sel, 3600, 48).catch(() => []),
        container.chain.dexOrders(sel, (address || undefined) as Address | undefined).catch(() => []),
        container.chain.dexTrades(sel, 20).catch(() => []),
      ]);
      setCandles(c ?? []);
      setOrders(o ?? []);
      setTrades(t ?? []);
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    }
  }, [sel, address]);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel]);

  const submit = async (tx: Record<string, unknown>, okMsg: string): Promise<void> => {
    setBusy(true);
    try {
      await container.chain.dexSubmit(tx);
      useUiStore.getState().pushToast('success', okMsg);
      await loadDetail();
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusy(false);
    }
  };

  const doSwap = (): void => {
    if (!pool || !amt.trim()) return;
    void submit(
      {
        type: 'swap',
        from: address,
        to: address,
        pool_address: sel,
        token0: side === 'in' ? pool.token0 : pool.token1,
        token1: side === 'in' ? pool.token1 : pool.token0,
        amount: Number(amt),
      },
      '兑换已提交',
    );
  };

  const doAddLiq = (): void => {
    if (!pool || !amt.trim()) return;
    void submit(
      { type: 'add_liquidity', from: address, pool_address: sel, token: oToken || pool.token0, amount: Number(amt) },
      '注入流动性已提交',
    );
  };

  const doOrder = (): void => {
    if (!pool || !oPrice.trim() || !oAmt.trim()) return;
    void submit(
      {
        type: 'order',
        action: 'place',
        from: address,
        pool_address: sel,
        side: oSide,
        token: oToken || pool.token1,
        price: Number(oPrice),
        amount: Number(oAmt),
      },
      '挂单已提交',
    );
  };

  const cancelOrder = (orderId: string): void => {
    void submit({ type: 'order', action: 'cancel', from: address, pool_address: sel, order_id: orderId }, '撤单已提交');
  };

  return (
    <div className="space-y-4">
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">交易对</h2>
        {pools.length === 0 ? (
          <EmptyState title="暂无交易对" description="等待创建流动性池" />
        ) : (
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
            {pools.map((p) => (
              <button
                key={p.pool_address}
                type="button"
                onClick={() => setSel(p.pool_address as string)}
                className={`rounded-btn border px-2.5 py-2 text-left text-xs transition ${
                  sel === p.pool_address ? 'border-accent bg-panel-2' : 'border-line bg-panel'
                }`}
              >
                <div className="font-medium text-text">{p.token0}/{p.token1}</div>
                <div className="mt-0.5 text-dim">价 {fmt(p.price ?? 0)}</div>
              </button>
            ))}
          </div>
        )}
      </section>

      {pool ? (
        <>
          <section className="card p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-medium text-text">
                {pool.token0}/{pool.token1}
              </h2>
              <Spark candles={candles} />
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-xs text-dim">
              <Badge tone="neutral">储备 {fmt(pool.reserve0)} {pool.token0}</Badge>
              <Badge tone="neutral">储备 {fmt(pool.reserve1)} {pool.token1}</Badge>
              <Badge tone="ok">价格 {fmt(pool.price ?? 0)}</Badge>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-btn bg-panel-2 p-3">
                <div className="mb-2 text-xs font-medium text-text">兑换</div>
                <div className="flex items-center gap-2">
                  <Select
                    value={side}
                    onChange={(v) => setSide(v as 'in' | 'out')}
                    className="shrink-0"
                    options={[
                      { value: 'in', label: `卖 ${pool.token0}` },
                      { value: 'out', label: `买 ${pool.token0}` },
                    ]}
                  />
                  <Input value={amt} onChange={(e) => setAmt(e.target.value)} placeholder="数量" inputMode="decimal" />
                </div>
                <Button size="sm" loading={busy} className="mt-2 w-full" onClick={doSwap}>
                  兑换
                </Button>
              </div>

              <div className="rounded-btn bg-panel-2 p-3">
                <div className="mb-2 text-xs font-medium text-text">注入流动性（{pool.token0}）</div>
                <Input value={oToken} onChange={(e) => setOToken(e.target.value)} placeholder="代币（默认 token0）" />
                <Input value={amt} onChange={(e) => setAmt(e.target.value)} placeholder="数量" className="mt-2" inputMode="decimal" />
                <Button size="sm" loading={busy} className="mt-2 w-full" variant="outline" onClick={doAddLiq}>
                  注入
                </Button>
              </div>
            </div>
          </section>

          <section className="card p-5">
            <h2 className="mb-3 text-sm font-medium text-text">限价挂单</h2>
            <div className="grid gap-2 sm:grid-cols-4">
              <Select
                value={oSide}
                onChange={(v) => setOSide(v as 'buy' | 'sell')}
                options={[
                  { value: 'buy', label: '买入' },
                  { value: 'sell', label: '卖出' },
                ]}
              />
              <Input value={oToken} onChange={(e) => setOToken(e.target.value)} placeholder={`代币（默认 ${pool.token1}）`} />
              <Input value={oPrice} onChange={(e) => setOPrice(e.target.value)} placeholder="价格" inputMode="decimal" />
              <Input value={oAmt} onChange={(e) => setOAmt(e.target.value)} placeholder="数量" inputMode="decimal" />
            </div>
            <Button size="sm" loading={busy} className="mt-2" onClick={doOrder}>
              挂单
            </Button>
            {orders.length > 0 ? (
              <ul className="mt-3 space-y-1.5">
                {orders.map((o) => (
                  <li key={o.order_id} className="flex items-center justify-between rounded-btn bg-panel-2 px-3 py-2 text-xs">
                    <Badge tone={o.side === 'buy' ? 'ok' : 'warn'}>{o.side === 'buy' ? '买' : '卖'}</Badge>
                    <span className="text-text">{o.token} @ {fmt(o.price)}</span>
                    <span className="text-dim">量 {fmt(o.amount)}</span>
                    <span className="mono text-dim">{shortId(o.order_id)}</span>
                    <Button variant="ghost" size="sm" onClick={() => cancelOrder(o.order_id)}>撤单</Button>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>

          <section className="card p-5">
            <h2 className="mb-3 text-sm font-medium text-text">最近成交</h2>
            {trades.length === 0 ? (
              <EmptyState title="暂无成交" />
            ) : (
              <ul className="space-y-1.5">
                {trades.map((t, i) => (
                  <li key={i} className="flex items-center justify-between rounded-btn bg-panel-2 px-3 py-2 text-xs">
                    <span className="text-text">
                      {t.type === 'swap'
                        ? `${fmt(t.amount_in ?? t.amount)} ${t.token_in ?? ''} → ${fmt(
                            typeof t.amount_out === 'object' && t.amount_out ? Object.values(t.amount_out)[0] : t.amount_out,
                          )} ${t.token_out ?? ''}`
                        : t.type === 'add_liquidity'
                          ? `注入 ${fmt(t.amount)} ${t.token ?? ''}`
                          : t.type === 'remove_liquidity'
                            ? `赎回 LP ${fmt(t.shares)}`
                            : t.type === 'create_pool'
                              ? `建池 ${t.token0 ?? ''}/${t.token1 ?? ''}`
                              : String(t.type ?? '')}
                    </span>
                    <span className="mono text-dim">#{t.height ?? t.ts ?? ''}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      ) : (
        <EmptyState title="选择交易对" description="点击上方交易对查看行情与交易" />
      )}
    </div>
  );
}

export default DexPage;
